/**
 * ONE answer to "does this message have attachments worth showing?".
 *
 * Three surfaces ask it — the thread-list paperclip, the reading pane's file
 * list, and the `Message` adapter that feeds the compose modal — and they must
 * agree, or a row advertises a clip that opens to nothing.
 *
 * Kept in its own dependency-free module (no `"use client"`, no `@wtc/ui`) so
 * the pure adapter can import the predicate without dragging a React component
 * tree behind it.
 */

import type { SupportInboxAttachment } from "./types";

/**
 * Whether an attachment is worth listing.
 *
 * Inline IMAGES are almost always signature logos and tracking pixels — listing
 * them buries the one file the operator is actually looking for. Everything else
 * is shown, including inline non-images, because a real document occasionally
 * arrives marked `Content-Disposition: inline`.
 */
export function isListableAttachment(attachment: SupportInboxAttachment): boolean {
  return !(attachment.inline && attachment.mimeType.startsWith("image/"));
}

/**
 * Whether a message has any attachment worth showing.
 *
 * `attachments` is optional on the wire: a response cached from a server build
 * that predates attachment support simply omits it, and that must read as "none
 * listed" rather than crash the reading pane.
 */
export function hasVisibleAttachments(attachments: SupportInboxAttachment[] | undefined): boolean {
  return (attachments ?? []).some(isListableAttachment);
}
